import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Shared";

const STEPS = [
  {
    num: "01",
    title: "Discover",
    desc: "We learn about your business, your clients and what success looks like for you.",
  },
  {
    num: "02",
    title: "Design",
    desc: "We shape the look, structure and content into a clear plan you approve before anything is built.",
  },
  {
    num: "03",
    title: "Build",
    desc: "We develop, test and refine, keeping you in the loop in plain language throughout.",
  },
  {
    num: "04",
    title: "Launch & Support",
    desc: "We go live, hand over the keys and stay on call for whatever comes next.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 60%"],
  });
  const scaleX = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section ref={ref} data-testid="process-section" className="py-24 md:py-32">
      <div className="container-x">
        <SectionHead
          eyebrow="How We Work"
          title="A simple, honest process from hello to hand-over."
          sub="No black boxes and no jargon, just four clear steps so you always know where things stand."
        />

        <div className="relative mt-16">
          <div
            className="absolute inset-x-0 top-[21px] hidden h-px bg-white/10 lg:block"
            aria-hidden="true"
          />
          <motion.div
            style={{ scaleX }}
            className="absolute inset-x-0 top-[21px] hidden h-px origin-left bg-gradient-to-r from-blue to-cy lg:block"
            aria-hidden="true"
          />
          <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1} data-testid={`process-step-${i + 1}`}>
                <span className="relative z-10 flex h-11 w-11 items-center justify-center rounded-full border border-cy/40 bg-ink font-mono text-xs text-cy">
                  {s.num}
                </span>
                <h3 className="mt-6 font-grotesk text-lg font-semibold tracking-tight text-txt">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-sub">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
